import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import Loader from 'react-loader-spinner';
import { FaWhatsapp, FaTimes, FaInfoCircle } from 'react-icons/fa';
import { FirebaseCustomers } from '../../../../firebase/functions';
import { getOnlyAlphaNums } from '../../../../utils/getOnlyAlphaNums';
import * as UserActions from '../../../../store/modules/user/actions';
import * as CartActions from '../../../../store/modules/cart/actions';
import * as SystemActions from '../../../../store/modules/system/actions';
import * as AddressesActions from '../../../../store/modules/addresses/actions';
import * as S from './styles';

const toE164 = (digits) => {
  let d = digits || '';
  if (d.length <= 11 && !d.startsWith('55')) d = `55${d}`;
  return `+${d}`;
};

const formatForDisplay = (digits) => {
  let d = digits || '';
  if (d.startsWith('55') && d.length > 11) d = d.slice(2);
  if (d.length === 11) return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
  if (d.length === 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return digits;
};

// O telefone confiavel sai sempre do jid (@c.us), nunca do @lid.
const classifyContact = (contact) => {
  if (!contact || !contact.id) return null;

  const id = String(contact.id || '');
  const jid = typeof contact.jid === 'string' ? contact.jid : '';
  const name = contact.name || contact.pushname || contact.formattedName || '';
  // O @lid (id interno de privacidade do WhatsApp) vai para um campo proprio
  // (whatsappId), separado do telefone real (phone). Guardamos COM o sufixo
  // "@lid" ("<digitos>@lid"), nunca so os digitos. Vazio quando nao e @lid.
  const lidRaw = contact.lid || (id.includes('@lid') ? id : '');
  const lidDigits = lidRaw ? (getOnlyAlphaNums(String(lidRaw).split('@')[0]) || '') : '';
  const whatsappId = lidDigits ? `${lidDigits}@lid` : '';
  const base = { key: id, name, whatsappId };

  if (contact.isGroup || id.includes('@g.us') || jid.includes('@g.us')) {
    return { ...base, kind: 'group' };
  }
  if (id.includes('@broadcast') || jid.includes('@broadcast')) {
    return { ...base, kind: 'invalid' };
  }

  let digits = '';
  if (jid.includes('@c.us')) digits = getOnlyAlphaNums(jid.split('@')[0]) || '';
  if (!digits && contact.number) digits = getOnlyAlphaNums(String(contact.number)) || '';
  if (!digits && id.includes('@c.us')) digits = getOnlyAlphaNums(id.split('@')[0]) || '';

  if (digits === '0' || /^0+$/.test(digits)) return { ...base, kind: 'official' };

  const isLid = id.includes('@lid') || (!!contact.lid && !jid.includes('@c.us'));
  if (isLid && (!digits || digits.length < 10)) return { ...base, kind: 'lid' };
  if (!digits || digits.length < 10) return { ...base, kind: 'invalid' };

  return {
    ...base,
    kind: 'contact',
    digits,
    e164: toE164(digits),
    display: formatForDisplay(digits),
  };
};

/**
 * Campos a completar num cliente que JA EXISTE, a partir do contato:
 *   jid -> phone (numero real)   |   lid -> whatsappId
 * Regras (pra funcionar sem erro): so preenche o campo VAZIO e nunca grava um
 * valor que ja pertence a OUTRO cliente (evita telefone/LID duplicado).
 * `byLid`/`byPhone` sao os clientes achados por cada caminho (null se nenhum).
 */
const buildCustomerPatch = (contact, found, byLid, byPhone) => {
  const patch = {};
  if (contact.e164 && !found.phone && (!byPhone || byPhone.id === found.id)) {
    patch.phone = contact.e164;
  }
  if (contact.whatsappId && !found.whatsappId && (!byLid || byLid.id === found.id)) {
    patch.whatsappId = contact.whatsappId;
  }
  return patch;
};

const BLOCK_REASONS = {
  group: 'Chat de grupo do WhatsApp — não é possível finalizar pedido com este contato.',
  official: 'Número oficial do WhatsApp (anúncios) — não é possível finalizar pedido.',
};

// Monta o card (tom, textos e acao) a partir do contato e do resultado da busca.
// Fica fora do componente porque e pura — o componente so memoiza. O action.kind
// diz pro JSX qual handler chamar; o handler mesmo fica no componente.
const buildContactView = (contact, resolution) => {
  if (!contact) return null;

  if (contact.kind === 'group') {
    return {
      tone: 'block',
      title: 'Grupo do WhatsApp',
      subtitle: 'Grupos não geram pedido. Selecione o cliente manualmente.',
    };
  }
  if (contact.kind === 'official') {
    return {
      tone: 'block',
      title: 'Número oficial do WhatsApp',
      subtitle: 'É o número de anúncios do WhatsApp — não gera pedido.',
    };
  }
  if (contact.kind === 'invalid') {
    return {
      tone: 'block',
      title: 'Contato inválido',
      subtitle: 'Este contato não pode gerar pedido. Selecione o cliente manualmente.',
    };
  }
  if (contact.kind === 'lid') {
    return {
      tone: 'warn',
      title: contact.name || 'Cliente do WhatsApp',
      subtitle: 'Este contato não tem número de WhatsApp disponível.',
      note: 'Mesmo assim, ele receberá os avisos por mensagem pelo ID do WhatsApp.',
    };
  }

  const res = resolution && resolution.key === contact.key
    ? resolution
    : { status: 'searching', customer: null };
  const { status, customer } = res;

  if (status === 'searching') {
    return {
      tone: 'ok',
      loading: true,
      title: 'Carregando dados do cliente do WhatsApp',
      subtitle: contact.display,
    };
  }
  if (status === 'selected') {
    const selectedName = (customer && customer.name) || contact.name || contact.display;
    const title = `Cliente selecionado: ${selectedName}`;
    return { tone: 'ok', title, subtitle: 'preenchido pelo WhatsApp' };
  }
  if (status === 'created') {
    const createdName = (customer && customer.name) || contact.name || contact.display;
    const title = `Cliente cadastrado: ${createdName}`;
    return { tone: 'ok', title, subtitle: 'criado a partir do WhatsApp' };
  }
  if (status === 'matched' && customer) {
    const title = `Cliente do WhatsApp: ${customer.name}`;
    return {
      tone: 'ok',
      title,
      subtitle: contact.display,
      action: { label: 'Selecionar este cliente', kind: 'select', customer },
    };
  }
  const title = contact.name || 'Cliente do WhatsApp';
  const subtitle = `${contact.display} · não cadastrado`;
  return {
    tone: 'ok',
    title,
    subtitle,
    action: { label: 'Usar estes dados', kind: 'fill' },
  };
};

const WhatsappClientSuggestion = ({
  active,
  hasClientSelected,
  resetUser,
  addUserId,
  addUserName,
  addUserPhone,
  resetCartDeliveryState,
  addAdressesOnAdressGroup,
  setWhatsappOrderBlock,
}) => {
  const [contact, setContact] = useState(null);
  const [resolution, setResolution] = useState(null);
  const [dismissedKey, setDismissedKey] = useState(null);

  const cacheRef = useRef({});
  const hasClientSelectedRef = useRef(hasClientSelected);
  useEffect(() => { hasClientSelectedRef.current = hasClientSelected; }, [hasClientSelected]);

  const selectCustomer = useCallback((c) => {
    resetUser();
    resetCartDeliveryState();
    addUserId(c.id);
    addUserName(c.name);
    addUserPhone(c.phone);
    if (Array.isArray(c.addresses) && c.addresses.length >= 1) {
      addAdressesOnAdressGroup(c.addresses);
    }
  }, [
    resetUser, resetCartDeliveryState, addUserId,
    addUserName, addUserPhone, addAdressesOnAdressGroup,
  ]);

  useEffect(() => {
    // Integracao inativa: zera o card pra nao reaproveitar o contato anterior
    // quando o painel voltar a ficar ativo.
    if (!active) {
      setContact(null);
      setResolution(null);
      return undefined;
    }

    const onMessage = (event) => {
      const data = event && event.data;
      if (!data || data.source !== 'flash-gateway' || data.type !== 'active-chat') return;
      // Sem chat aberto (null): limpa, senao a conversa nova herda o contato
      // anterior e aparece como ja cadastrado.
      if (!data.contact) {
        setContact(null);
        setResolution(null);
        return;
      }
      const classified = classifyContact(data.contact);
      if (classified) setContact(classified);
    };
    const requestCurrent = () => {
      try {
        window.postMessage({ source: 'flash-platform', type: 'request-active-chat' }, '*');
      } catch (_) { /* noop */ }
    };
    const onVisibility = () => {
      if (document.visibilityState === 'visible') requestCurrent();
    };

    window.addEventListener('message', onMessage);
    window.addEventListener('focus', requestCurrent);
    document.addEventListener('visibilitychange', onVisibility);
    requestCurrent();

    return () => {
      window.removeEventListener('message', onMessage);
      window.removeEventListener('focus', requestCurrent);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [active]);

  // Bloqueia o botao de finalizar quando o chat aberto e grupo ou numero oficial.
  useEffect(() => {
    const kind = active && contact ? contact.kind : null;
    setWhatsappOrderBlock(!!BLOCK_REASONS[kind], BLOCK_REASONS[kind] || '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, contact]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => () => setWhatsappOrderBlock(false, ''), []);

  useEffect(() => {
    if (!active || !contact || contact.kind !== 'contact') return undefined;

    const cached = cacheRef.current[contact.key];
    if (cached) {
      setResolution({ key: contact.key, ...cached });
      return undefined;
    }
    setResolution({ key: contact.key, status: 'searching', customer: null });

    let cancelled = false;
    (async () => {
      // Busca pelos DOIS caminhos (LID e telefone). Rodar os dois permite detectar
      // conflito (LID e telefone em clientes diferentes) e completar so o que falta.
      let byLid = null;
      let byPhone = null;
      try {
        if (contact.whatsappId) {
          byLid = await FirebaseCustomers.getCustomerByWhatsappId(contact.whatsappId);
        }
        if (contact.digits) {
          byPhone = await FirebaseCustomers.getCustomerByPhoneNumber(contact.digits);
        }
      } catch (_) {
        byLid = null;
        byPhone = null;
      }
      if (cancelled) return;

      let found = byLid || byPhone; // prioriza o match pelo LID
      let next;
      if (found && found.id) {
        // Cliente ja existe: completa jid->phone e lid->whatsappId (so o que falta,
        // sem sobrescrever nem duplicar). Segue o fluxo mesmo se a atualizacao falhar.
        const patch = buildCustomerPatch(contact, found, byLid, byPhone);
        if (Object.keys(patch).length) {
          try {
            await FirebaseCustomers.updateCustomer(found.id, patch);
            if (cancelled) return;
            found = { ...found, ...patch };
          } catch (_) { /* nao bloqueia a selecao */ }
        }
        if (!hasClientSelectedRef.current) {
          selectCustomer(found);
          next = { status: 'selected', customer: found };
        } else {
          next = { status: 'matched', customer: found };
        }
      } else {
        // Nao achou no banco: NAO cria nada automaticamente. Mostra o botao
        // "Usar estes dados" e so cria quando o usuario clicar (handleFillNewClient).
        next = { status: 'nomatch', customer: null };
      }
      cacheRef.current[contact.key] = next;
      setResolution({ key: contact.key, ...next });
    })();

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, contact]);

  const applyResolution = (next) => {
    if (!contact) return;
    cacheRef.current[contact.key] = next;
    setResolution({ key: contact.key, ...next });
  };

  // "Usar estes dados": cria o cliente AGORA (nunca automaticamente) e seleciona.
  // Grava phone = numero real (jid) e whatsappId = LID, ambos quando existem.
  const handleFillNewClient = async () => {
    if (!contact || contact.kind !== 'contact') return;
    try {
      const createdId = await FirebaseCustomers.postCustomer({
        name: contact.name || contact.display,
        phone: contact.e164 || '',
        whatsappId: contact.whatsappId,
      });
      const created = {
        id: createdId,
        name: contact.name || contact.display,
        phone: contact.e164 || '',
        whatsappId: contact.whatsappId,
        addresses: [],
      };
      selectCustomer(created);
      applyResolution({ status: 'created', customer: created });
    } catch (_) {
      applyResolution({ status: 'nomatch', customer: null });
    }
  };

  const handleSelectExisting = (customer) => {
    if (!customer) return;
    selectCustomer(customer);
    applyResolution({ status: 'selected', customer });
  };

  // memoiza pra so refazer o card quando o contato ou a busca mudam
  const view = useMemo(() => buildContactView(contact, resolution), [contact, resolution]);

  if (!active || !contact || !view) return null;
  if (dismissedKey === contact.key) return null;

  const {
    tone = 'ok', title = '', subtitle = '', loading = false, action = null, note = null,
  } = view;

  return (
    <S.Card $tone={tone}>
      {tone !== 'block' && (
        <S.Close type="button" aria-label="Dispensar" onClick={() => setDismissedKey(contact.key)}>
          <FaTimes size={13} />
        </S.Close>
      )}
      <S.Icon $tone={tone}>
        {loading ? (
          <Loader type="TailSpin" color="#fff" height={20} width={20} />
        ) : (
          <FaWhatsapp size={20} />
        )}
      </S.Icon>
      <S.Content>
        <strong>{title}</strong>
        <span>{subtitle}</span>
        {action && (
          <S.Action
            type="button"
            onClick={action.kind === 'select'
              ? () => handleSelectExisting(action.customer)
              : handleFillNewClient}
          >
            {action.label}
          </S.Action>
        )}
        {note && (
          <S.Note>
            <FaInfoCircle size={12} />
            {note}
          </S.Note>
        )}
      </S.Content>
    </S.Card>
  );
};

WhatsappClientSuggestion.propTypes = {
  active: PropTypes.bool.isRequired,
  hasClientSelected: PropTypes.bool.isRequired,
  resetUser: PropTypes.func.isRequired,
  addUserId: PropTypes.func.isRequired,
  addUserName: PropTypes.func.isRequired,
  addUserPhone: PropTypes.func.isRequired,
  resetCartDeliveryState: PropTypes.func.isRequired,
  addAdressesOnAdressGroup: PropTypes.func.isRequired,
  setWhatsappOrderBlock: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
  active: !!state.establishment.isOnlineCommand
    && state.establishment.iframeRunningOn === 'painel',
  hasClientSelected: !!state.user.name,
});

const mapDispatchToProps = (dispatch) => bindActionCreators(
  {
    ...UserActions, ...CartActions, ...SystemActions, ...AddressesActions,
  },
  dispatch,
);

export default connect(mapStateToProps, mapDispatchToProps)(WhatsappClientSuggestion);
